import React, { useMemo, useState } from 'react';
import { buildUiHelpers } from './sharedUi';
import { formatDate, formatDateTime } from './sharedFormatters';

const REFERENCE_OPTIONS = [
  { value: 'CUSTOM', label: 'Custom-Index', currency: null },
  { value: 'SPY', label: 'S&P 500', currency: 'USD' },
  { value: 'QQQ', label: 'Nasdaq 100', currency: 'USD' },
  { value: '^GDAXI', label: 'DAX', currency: 'EUR' },
  { value: '^STOXX', label: 'STOXX 600', currency: 'EUR' },
  { value: 'URTH', label: 'MSCI World', currency: 'USD' },
];

const CURRENCY_OPTIONS = ['EUR', 'USD', 'CHF', 'GBP'];

function referenceLabel(symbol) {
  return REFERENCE_OPTIONS.find(option => option.value === symbol)?.label || symbol || '—';
}

export default function CustomRslWatchlistsSection({
  watchlists,
  loading,
  error,
  createWatchlist,
  updateWatchlist,
  deleteWatchlist,
  addItem,
  deleteItem,
  runWatchlists,
  reportIssues,
  previewTradingView,
  importTradingView,
  isAdmin,
  depot,
  kaufen,
  entfernen,
  speichernDepotPosition,
  addDepotTranche,
  deleteDepotTranche,
  tickerInZwischenablage,
  uiSettings,
  themeMode,
  COLORS,
  th,
  td,
  inputStyle,
  ScreenerTableComponent,
}) {
  const { Card, Btn } = useMemo(() => buildUiHelpers({ COLORS, th }), [COLORS, th]);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [reference, setReference] = useState('CUSTOM');
  const [currency, setCurrency] = useState('EUR');
  const [symbol, setSymbol] = useState('');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editReference, setEditReference] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [symbolAmbiguities, setSymbolAmbiguities] = useState([]);
  const [symbolSelections, setSymbolSelections] = useState({});
  const [running, setRunning] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportedKey, setReportedKey] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importSelected, setImportSelected] = useState([]);
  const [importName, setImportName] = useState('');
  const [importReference, setImportReference] = useState('CUSTOM');
  const [importCurrency, setImportCurrency] = useState('USD');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReporting, setImportReporting] = useState(false);
  const [importReportedKey, setImportReportedKey] = useState('');
  const [visibleResultRows, setVisibleResultRows] = useState({});
  const [notice, setNotice] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const selected = watchlists.find(row => row.id === selectedId) || watchlists[0] || null;
  const maxWatchlists = isAdmin ? 20 : 2;
  const selectedVisibleRows = selected ? (visibleResultRows[selected.id] || 10) : 10;
  const visibleResults = selected?.results?.slice(0, selectedVisibleRows) || [];
  const hasMoreResults = Boolean(selected?.results?.length > visibleResults.length);
  const guideSteps = isAdmin
    ? [
        'Lege eine Watchlist an oder importiere eine öffentliche TradingView-Watchlist.',
        'Wähle eine Referenz. Bei Custom-Index wird der RSL aus den Titeln der Watchlist berechnet.',
        'Füge Ticker hinzu. Admins können zusätzlich Ticker per Texteingabe ergänzen.',
        'Die RSL-Werte und Kurse werden samstags automatisch aktualisiert; Admins können den Lauf manuell starten.',
      ]
    : [
        'Importiere eine öffentliche TradingView-Watchlist.',
        'Wähle eine Referenz. Bei Custom-Index wird der RSL aus den Titeln der Watchlist berechnet.',
        'Die RSL-Werte und Kurse werden samstags automatisch aktualisiert; nach dem ersten Lauf erscheint die vollständige Signaltabelle.',
        'Nicht auswertbare Titel erscheinen separat und können als Fehler gemeldet werden.',
      ];

  const submitCreate = async () => {
    await createWatchlist({ name, reference_symbol: reference, currency });
    setName('');
    setReference('CUSTOM');
    setShowCreate(false);
  };

  const submitItem = async () => {
    if (!selected) return;
    const symbols = symbol
      .split(/[,;\n]+/)
      .map(value => value.trim().toUpperCase())
      .filter(Boolean);
    if (symbols.length === 0) return;
    const result = await addItem(selected.id, {
      items: symbols.map(value => ({
        symbol: value,
        yahoo_symbol: symbolSelections[value]?.yahoo_symbol || '',
      })),
    });
    if (Array.isArray(result?.ambiguities) && result.ambiguities.length > 0) {
      setSymbolAmbiguities(result.ambiguities);
      return;
    }
    if (!result?.ok) return;
    setSymbol('');
    setSymbolAmbiguities([]);
    setSymbolSelections({});
  };

  const startEditing = () => {
    if (!selected) return;
    setEditName(selected.name || '');
    setEditReference(selected.reference_symbol || '');
    setEditCurrency(selected.currency || '');
    setEditing(true);
  };

  const submitEdit = async () => {
    if (!selected) return;
    await updateWatchlist(selected.id, {
      name: editName,
      reference_symbol: editReference,
      currency: editCurrency,
    });
    setEditing(false);
  };

  const openDeleteWatchlistConfirm = () => {
    if (!selected) return;
    setDeleteConfirm(selected);
  };

  const confirmDeleteWatchlist = async () => {
    if (!deleteConfirm || deleteSaving) return;
    setDeleteSaving(true);
    try {
      await deleteWatchlist(deleteConfirm.id);
      setDeleteConfirm(null);
    } finally {
      setDeleteSaving(false);
    }
  };

  const selectReference = (value, setter, currencySetter) => {
    setter(value);
    const option = REFERENCE_OPTIONS.find(entry => entry.value === value);
    if (option?.currency) currencySetter(option.currency);
  };

  const runNow = async () => {
    setRunning(true);
    setNotice('');
    try {
      const result = await runWatchlists?.();
      if (result?.run?.message) {
        setNotice(result.run.message);
      }
    } finally {
      setRunning(false);
    }
  };

  const reportFailedItems = async () => {
    if (!selected?.failed_items?.length) return;
    const issueLines = selected.failed_items.map(item => (
      `- ${item.symbol}${item.yahoo_symbol && item.yahoo_symbol !== item.symbol ? ` (Yahoo: ${item.yahoo_symbol})` : ''}`
    ));
    const snapshot = selected.snapshot || {};
    const message = [
      'Bei einer eigenen RSL-Watchlist konnten Titel nicht ausgewertet werden.',
      '',
      `Watchlist: ${selected.name}`,
      `Watchlist-ID: ${selected.id}`,
      `Referenz: ${referenceLabel(selected.reference_symbol)}`,
      `Datenanbieter: ${snapshot.provider || '—'}`,
      `Berechnet: ${snapshot.calculated_at ? formatDateTime(snapshot.calculated_at) : '—'}`,
      `Datenstand: ${snapshot.data_as_of ? formatDate(snapshot.data_as_of) : '—'}`,
      '',
      'Nicht auswertbare Titel:',
      ...issueLines,
    ].join('\n');
    setReporting(true);
    try {
      const result = await reportIssues?.({
        subject: `Eigene RSL-Watchlist: fehlerhafte Titel in ${selected.name}`,
        category: 'RSL-Scanner',
        message,
      });
      if (result?.ok) {
        setReportedKey(`${selected.id}:${snapshot.id || ''}`);
      }
    } finally {
      setReporting(false);
    }
  };

  const loadImportPreview = async () => {
    if (!importUrl.trim()) return;
    setPreviewLoading(true);
    setNotice('');
    try {
      const result = await previewTradingView?.(importUrl.trim());
      if (!result?.ok || !result.data) return;
      setImportPreview(result.data);
      setImportName(result.data.name || '');
      setImportSelected((result.data.resolved || []).map(item => item.tradingview_symbol));
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleImportSymbol = (tradingviewSymbol) => {
    setImportSelected(current => (
      current.includes(tradingviewSymbol)
        ? current.filter(symbolValue => symbolValue !== tradingviewSymbol)
        : [...current, tradingviewSymbol]
    ));
  };

  const submitImport = async () => {
    if (!importPreview || importSelected.length === 0 || importSelected.length > 100) return;
    setImporting(true);
    setNotice('');
    try {
      const result = await importTradingView?.({
        source_url: importPreview.source_url,
        name: importName,
        reference_symbol: importReference,
        currency: importCurrency,
        selected_symbols: importSelected,
      });
      if (!result?.ok) return;
      if (result?.autoRun?.message) {
        setNotice(result.autoRun.message);
      }
      setShowImport(false);
      setImportUrl('');
      setImportPreview(null);
      setImportSelected([]);
      setImportName('');
    } finally {
      setImporting(false);
    }
  };

  const reportImportIssues = async () => {
    if (!importPreview?.not_found?.length) return;
    const issuesByReason = importPreview.not_found.reduce((groups, item) => {
      const reason = item.reason || 'Kein Fehlergrund vorhanden';
      const symbolValue = item.tradingview_symbol || item.app_symbol || 'Unbekannt';
      if (!groups[reason]) groups[reason] = [];
      groups[reason].push(symbolValue);
      return groups;
    }, {});
    const issueSections = Object.entries(issuesByReason).flatMap(([reason, symbols]) => [
      `${reason} (${symbols.length}):`,
      symbols.join(', '),
      '',
    ]);
    const messageParts = [
      'Beim Import einer öffentlichen TradingView-Watchlist konnten Titel nicht zugeordnet werden.',
      '',
      `Watchlist: ${importName || importPreview.name || '—'}`,
      `Quelle: ${importPreview.source_url || importUrl || '—'}`,
      `Referenz: ${referenceLabel(importReference)}`,
      `Währung: ${importCurrency || '—'}`,
      '',
      `Nicht gefundene Titel: ${importPreview.not_found.length}`,
      '',
      ...issueSections,
    ];
    const fullMessage = messageParts.join('\n').trim();
    const message = fullMessage.length <= 4800
      ? fullMessage
      : `${fullMessage.slice(0, 4700).trim()}\n\n[Liste wegen des Ticketlimits gekürzt]`;
    const reportKey = `${importPreview.source_url || importUrl}:${importPreview.not_found.map(item => item.tradingview_symbol).join(',')}`;
    setImportReporting(true);
    try {
      const result = await reportIssues?.({
        subject: `Eigene RSL-Watchlist: nicht gefundene Titel in ${importName || importPreview.name || 'TradingView-Import'}`,
        category: 'RSL-Scanner',
        message,
      });
      if (result?.ok) {
        setImportReportedKey(reportKey);
      }
    } finally {
      setImportReporting(false);
    }
  };

  return (
    <Card style={{ marginTop: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div>
          <div style={{ fontWeight: 800 }}>Eigene RSL-Watchlisten</div>
          <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
            Wöchentliche Berechnung samstags.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isAdmin && (
            <Btn onClick={runNow} color="green" size="sm" disabled={running || watchlists.length === 0}>
              {running ? 'Lauf läuft...' : 'Lauf starten'}
            </Btn>
          )}
          <Btn onClick={() => setShowImport(value => !value)} color="ghost" size="sm" disabled={watchlists.length >= maxWatchlists}>
            Watchlist importieren
          </Btn>
          {isAdmin && (
            <Btn onClick={() => setShowCreate(value => !value)} color="cyan" size="sm" disabled={watchlists.length >= maxWatchlists}>
              Neue Watchlist
            </Btn>
          )}
        </div>
      </div>

      {error && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {error}</div>}
      {notice && <div style={{ color: COLORS.cyan || '#21d4d8', fontSize: '13px', marginBottom: '12px' }}>✓ {notice}</div>}
      {loading && <div style={{ color: COLORS.muted, fontSize: '13px', marginBottom: '12px' }}>Lade Watchlists...</div>}

      <div style={{
        marginBottom: '16px',
        padding: '12px 14px',
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        background: '#ffffff04',
        color: COLORS.muted,
        fontSize: '13px',
        lineHeight: 1.65,
      }}>
        <div style={{ color: COLORS.text, fontWeight: 800, marginBottom: '6px' }}>
          Kurzanleitung Scanner Eigene RSL-Watchlisten
        </div>
        {guideSteps.map((step, index) => (
          <div key={step}>{index + 1}. {step}</div>
        ))}
      </div>

      {showImport && (
        <div style={{ padding: '14px 0 18px', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 1fr) auto', gap: '10px', alignItems: 'end' }}>
            <label style={{ fontSize: '12px', color: COLORS.muted }}>Öffentliche TradingView-Watchlist
              <input
                value={importUrl}
                onChange={event => {
                  setImportUrl(event.target.value);
                  setImportPreview(null);
                  setImportSelected([]);
                  setImportReportedKey('');
                }}
                placeholder="https://de.tradingview.com/watchlists/..."
                style={{ ...inputStyle, marginTop: '5px', marginBottom: 0 }}
              />
            </label>
            <Btn onClick={loadImportPreview} color="cyan" size="sm" disabled={previewLoading}>
              {previewLoading ? 'Wird geprüft...' : 'Vorschau laden'}
            </Btn>
          </div>

          {importPreview && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(150px, .7fr) 100px', gap: '10px', marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', color: COLORS.muted }}>Name
                  <input value={importName} onChange={event => setImportName(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }} />
                </label>
                <label style={{ fontSize: '12px', color: COLORS.muted }}>Referenz
                  <select
                    value={importReference}
                    onChange={event => selectReference(event.target.value, setImportReference, setImportCurrency)}
                    style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}
                  >
                    {REFERENCE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <label style={{ fontSize: '12px', color: COLORS.muted }}>Währung
                  <select value={importCurrency} onChange={event => setImportCurrency(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}>
                    {CURRENCY_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', color: COLORS.muted, fontSize: '12px', marginBottom: '10px' }}>
                <span>Gesamt: <strong style={{ color: COLORS.text }}>{importPreview.total}</strong></span>
                <span>Erkannt: <strong style={{ color: COLORS.green }}>{importPreview.resolved?.length || 0}</strong></span>
                <span>Mehrdeutig: <strong style={{ color: COLORS.yellow }}>{importPreview.ambiguous?.length || 0}</strong></span>
                <span>Nicht gefunden: <strong style={{ color: COLORS.red }}>{importPreview.not_found?.length || 0}</strong></span>
                <span>Ausgewählt: <strong style={{ color: importSelected.length > 100 ? COLORS.red : COLORS.text }}>{importSelected.length}/100</strong></span>
              </div>

              {importSelected.length > 100 && (
                <div style={{ color: COLORS.red, fontSize: '12px', marginBottom: '10px' }}>
                  Bitte noch {importSelected.length - 100} Titel abwählen.
                </div>
              )}

              <div style={{ maxHeight: '280px', overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: '8px', padding: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '4px' }}>
                {(importPreview.resolved || []).map(item => (
                  <label key={item.tradingview_symbol} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', padding: '6px', fontSize: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={importSelected.includes(item.tradingview_symbol)}
                      onChange={() => toggleImportSymbol(item.tradingview_symbol)}
                    />
                    <span>
                      <span style={{ color: COLORS.text, fontWeight: 700 }}>
                        {item.tradingview_symbol}{item.name ? ` · ${item.name}` : ''}
                      </span>
                      {item.industry && (
                        <span style={{ display: 'block', color: COLORS.muted, marginTop: '2px' }}>
                          {item.industry}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>

              {importPreview.ambiguous?.length > 0 && (
                <div style={{ color: COLORS.yellow, fontSize: '12px', marginTop: '10px' }}>
                  Mehrdeutig: {importPreview.ambiguous.map(item => item.tradingview_symbol).join(', ')}
                </div>
              )}
              {importPreview.not_found?.length > 0 && (
                <div style={{ color: COLORS.red, fontSize: '12px', marginTop: '8px' }}>
                  Nicht importierbar: {importPreview.not_found.map(item => item.tradingview_symbol).join(', ')}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                {importPreview.not_found?.length > 0 && (
                  <Btn
                    onClick={reportImportIssues}
                    color="ghost"
                    size="sm"
                    disabled={
                      importReporting
                      || importReportedKey === `${importPreview.source_url || importUrl}:${importPreview.not_found.map(item => item.tradingview_symbol).join(',')}`
                    }
                  >
                    {importReporting
                      ? 'Wird gemeldet...'
                      : importReportedKey === `${importPreview.source_url || importUrl}:${importPreview.not_found.map(item => item.tradingview_symbol).join(',')}`
                        ? 'Als Ticket gemeldet'
                        : 'Nicht gefundene Titel melden'}
                  </Btn>
                )}
                <Btn onClick={submitImport} color="green" size="sm" disabled={importing || importSelected.length === 0 || importSelected.length > 100}>
                  {importing ? 'Wird importiert...' : `${importSelected.length} Titel importieren`}
                </Btn>
              </div>
            </div>
          )}
        </div>
      )}

      {isAdmin && showCreate && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(150px, .7fr) 100px auto', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', color: COLORS.muted }}>Name
            <input value={name} onChange={event => setName(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }} />
          </label>
          <label style={{ fontSize: '12px', color: COLORS.muted }}>Referenz
            <select
              value={reference}
              onChange={event => selectReference(event.target.value, setReference, setCurrency)}
              style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}
            >
              {REFERENCE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: '12px', color: COLORS.muted }}>Währung
            <select value={currency} onChange={event => setCurrency(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}>
              {CURRENCY_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <Btn onClick={submitCreate} color="green" size="sm">Anlegen</Btn>
        </div>
      )}

      {watchlists.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {watchlists.map(row => (
            <button
              type="button"
              key={row.id}
              onClick={() => {
                setSelectedId(row.id);
                setVisibleResultRows(current => ({ ...current, [row.id]: 10 }));
              }}
              style={{
                border: `1px solid ${(selected?.id === row.id) ? COLORS.accent : COLORS.border}`,
                background: (selected?.id === row.id) ? COLORS.accent : '#ffffff08',
                color: (selected?.id === row.id) ? '#fff' : COLORS.text,
                borderRadius: '8px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              {row.name}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '16px',
            padding: '14px 16px',
            background: '#ffffff05',
            borderTop: `1px solid ${COLORS.border}`,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px' }}>
                <span style={{ color: COLORS.muted }}>
                  Referenz: <strong style={{ color: COLORS.text }}>{referenceLabel(selected.reference_symbol)}</strong>
                </span>
                <span style={{ color: COLORS.muted }}>
                  Titel: <strong style={{ color: COLORS.text }}>{selected.item_count}/100</strong>
                </span>
                {selected.snapshot?.reference_rsl != null && (
                  <span style={{ color: COLORS.muted }}>
                    Referenz-RSL: <strong style={{ color: COLORS.text }}>{Number(selected.snapshot.reference_rsl).toFixed(2)}</strong>
                  </span>
                )}
              </div>
              <div style={{ color: COLORS.muted, fontSize: '11px', marginTop: '6px' }}>
                {selected.snapshot?.calculated_at && `Berechnet: ${formatDateTime(selected.snapshot.calculated_at)}`}
                {selected.snapshot?.data_as_of && ` · Datenstand: ${formatDate(selected.snapshot.data_as_of)}`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Btn onClick={startEditing} color="ghost" size="sm">Bearbeiten</Btn>
              <Btn onClick={openDeleteWatchlistConfirm} color="red" size="sm">Löschen</Btn>
            </div>
          </div>

          {editing && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) minmax(150px, .7fr) 100px auto auto', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: COLORS.muted }}>Name
                <input value={editName} onChange={event => setEditName(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }} />
              </label>
              <label style={{ fontSize: '12px', color: COLORS.muted }}>Referenz
                <select
                  value={editReference}
                  onChange={event => selectReference(event.target.value, setEditReference, setEditCurrency)}
                  style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}
                >
                  {REFERENCE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: '12px', color: COLORS.muted }}>Währung
                <select value={editCurrency} onChange={event => setEditCurrency(event.target.value)} style={{ ...inputStyle, height: '36px', boxSizing: 'border-box', marginTop: '5px', marginBottom: 0 }}>
                  {CURRENCY_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <Btn onClick={submitEdit} color="green" size="sm">Speichern</Btn>
              <Btn onClick={() => setEditing(false)} color="gray" size="sm">Abbrechen</Btn>
            </div>
          )}

          {isAdmin && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: COLORS.muted }}>Ticker
                <input
                  value={symbol}
                  onChange={event => {
                    setSymbol(event.target.value.toUpperCase());
                    setSymbolAmbiguities([]);
                    setSymbolSelections({});
                  }}
                  placeholder="AAPL, MSFT, SAP"
                  style={{ ...inputStyle, marginTop: '5px', marginBottom: 0 }}
                />
              </label>
              <Btn onClick={submitItem} color="green" size="sm">Titel hinzufügen</Btn>
            </div>
          )}

          {isAdmin && symbolAmbiguities.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px', border: `1px solid ${COLORS.border}`, borderRadius: '8px' }}>
              <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '8px' }}>
                Einige Ticker sind an mehreren Börsenplätzen verfügbar. Bitte jeweils auswählen:
              </div>
              {symbolAmbiguities.map(ambiguity => (
                <div key={ambiguity.symbol} style={{ marginTop: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '6px' }}>{ambiguity.symbol}</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(ambiguity.choices || []).map(choice => {
                      const selectedChoice = symbolSelections[ambiguity.symbol]?.yahoo_symbol === choice.yahoo_symbol;
                      return (
                        <Btn
                          key={`${ambiguity.symbol}-${choice.market}-${choice.yahoo_symbol}`}
                          onClick={() => setSymbolSelections(current => ({
                            ...current,
                            [ambiguity.symbol]: choice,
                          }))}
                          color={selectedChoice ? 'cyan' : 'ghost'}
                          size="sm"
                        >
                          {choice.name || choice.app_symbol} · {choice.market} · {choice.yahoo_symbol}
                        </Btn>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div style={{ marginTop: '12px' }}>
                <Btn
                  onClick={submitItem}
                  color="green"
                  size="sm"
                  disabled={symbolAmbiguities.some(ambiguity => !symbolSelections[ambiguity.symbol])}
                >
                  Auswahl übernehmen
                </Btn>
              </div>
            </div>
          )}

          {!selected.has_successful_run && selected.items.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {selected.items.map(item => (
                <span key={item.id} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', padding: '5px 8px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', fontSize: '12px' }}>
                  {item.symbol}
                  <button type="button" onClick={() => deleteItem(selected.id, item.id)} title="Titel entfernen" style={{ border: 0, background: 'transparent', color: COLORS.red, cursor: 'pointer', padding: 0 }}>×</button>
                </span>
              ))}
            </div>
          )}

          {selected.has_successful_run && selected.pending_items?.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: COLORS.muted, fontSize: '12px', marginBottom: '7px' }}>
                Noch nicht berechnet:
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {selected.pending_items.map(item => (
                  <span key={item.id} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', padding: '5px 8px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', fontSize: '12px' }}>
                    {item.symbol}
                    <button type="button" onClick={() => deleteItem(selected.id, item.id)} title="Titel entfernen" style={{ border: 0, background: 'transparent', color: COLORS.red, cursor: 'pointer', padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {selected.has_successful_run && selected.failed_items?.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '10px 12px', border: `1px solid ${COLORS.red}55`, borderRadius: '8px', background: `${COLORS.red}0d` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '7px' }}>
                <div style={{ color: COLORS.red, fontSize: '12px', fontWeight: 700 }}>
                  Nicht auswertbar:
                </div>
                <Btn
                  onClick={reportFailedItems}
                  color="ghost"
                  size="sm"
                  disabled={reporting || reportedKey === `${selected.id}:${selected.snapshot?.id || ''}`}
                >
                  {reporting
                    ? 'Wird gemeldet...'
                    : reportedKey === `${selected.id}:${selected.snapshot?.id || ''}`
                      ? 'Als Ticket gemeldet'
                      : 'Fehlerhafte Titel melden'}
                </Btn>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {selected.failed_items.map(item => (
                  <span key={item.id} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', padding: '5px 8px', border: `1px solid ${COLORS.red}44`, borderRadius: '6px', fontSize: '12px' }}>
                    {item.symbol}
                    <button type="button" onClick={() => deleteItem(selected.id, item.id)} title="Titel entfernen" style={{ border: 0, background: 'transparent', color: COLORS.red, cursor: 'pointer', padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {selected.snapshot?.status === 'error' && (
            <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '14px' }}>
              Die letzte Wochenberechnung war nicht vollständig: {selected.snapshot.error_message || 'Daten nicht verfügbar'}
            </div>
          )}

          {selected.items.length === 0 && (
            <div style={{ color: COLORS.muted, fontSize: '13px', padding: '16px 0' }}>
              Füge zuerst Titel hinzu. Die RSL-Auswertung wird anschließend samstags aktualisiert.
            </div>
          )}

          {selected.results.length > 0 && (
            <ScreenerTableComponent
              titel={visibleResults}
              marktLabel={`🧩 ${selected.name}`}
              marktKey="CUSTOM"
              currency={selected.currency || ''}
              customWatchlistId={selected.id}
              depot={depot}
              onKaufen={(ticker) => kaufen(ticker, 'CUSTOM', selected.id)}
              onEntfernen={entfernen}
              onSpeichernPosition={speichernDepotPosition}
              onAddTranche={addDepotTranche}
              onDeleteTranche={deleteDepotTranche}
              onCopyTickers={tickerInZwischenablage}
              chartsEnabled={uiSettings.tradingview_charts_enabled}
              themeMode={themeMode}
              COLORS={COLORS}
              th={th}
              td={td}
              inputStyle={inputStyle}
              showLoadMoreTopSignals={hasMoreResults}
              hasMoreTopRows={hasMoreResults}
              onLoadMoreTopRows={() => setVisibleResultRows(current => ({
                ...current,
                [selected.id]: Math.min(
                  (current[selected.id] || 10) + 10,
                  selected.results.length,
                ),
              }))}
              showDepotSection
              onRemoveSignal={(item) => deleteItem(selected.id, item.id)}
            />
          )}

        </>
      )}

      {deleteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-watchlist-delete-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            background: 'rgba(0, 8, 12, 0.72)',
            display: 'grid',
            placeItems: 'center',
            padding: '22px',
          }}
          onMouseDown={event => {
            if (event.target === event.currentTarget && !deleteSaving) {
              setDeleteConfirm(null);
            }
          }}
        >
          <Card style={{
            width: 'min(620px, 100%)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.42)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              marginBottom: '16px',
            }}>
              <div>
                <div style={{
                  color: COLORS.muted,
                  fontSize: '12px',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                }}>
                  Eigene RSL-Watchliste
                </div>
                <h3 id="custom-watchlist-delete-title" style={{
                  margin: 0,
                  color: COLORS.text,
                  fontSize: '22px',
                  lineHeight: 1.2,
                }}>
                  Watchlist löschen?
                </h3>
              </div>
              <span style={{
                border: '1px solid rgba(239, 68, 68, 0.35)',
                background: 'rgba(239, 68, 68, 0.12)',
                color: COLORS.red,
                borderRadius: '999px',
                padding: '5px 10px',
                fontSize: '12px',
                fontWeight: 800,
              }}>
                Löschen
              </span>
            </div>

            <div style={{
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff05',
              borderRadius: '14px',
              padding: '14px',
              color: COLORS.text,
              fontSize: '14px',
              lineHeight: 1.65,
              marginBottom: '14px',
            }}>
              <strong>{deleteConfirm.name}</strong> wird aus deinen eigenen RSL-Watchlisten entfernt.
              <br />
              Die Watchlist und ihre Scanner-Auswertung werden gelöscht. Bestehende Depot- und RSL-Journal-Daten bleiben erhalten.
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexWrap: 'wrap',
            }}>
              <Btn onClick={() => setDeleteConfirm(null)} color="ghost" disabled={deleteSaving}>
                Abbrechen
              </Btn>
              <Btn onClick={confirmDeleteWatchlist} color="red" disabled={deleteSaving}>
                {deleteSaving ? 'Lösche...' : 'Watchlist löschen'}
              </Btn>
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}
